import { useEffect, useState } from 'react';
import { cn } from '@/shared/lib/utils';
import { personaSourceLabel, type PersonaSignal } from '../utils/personaSignals';
import type { AudienceFact } from '../hooks/scanEventRouter';
import CustomersWaiting from './CustomersWaiting';

export interface CustomerPersonaCard {
  index: number;
  /** The group, e.g. "Travellers 55+, planning river cruises". Never a person. */
  segment: string;
  /** How much of the business it accounts for, e.g. "largest group in your reviews". */
  scope?: string;
  description?: string;
  signals?: PersonaSignal[];
}

// The prototype lets each trait land on its own so the source beside it is
// read, rather than dropping a finished row of chips in one beat.
const TRAIT_REVEAL_MS = 900;

function PersonaTraits({ signals }: { signals: PersonaSignal[] }) {
  const [shown, setShown] = useState(1);

  useEffect(() => { setShown(1); }, [signals]);

  useEffect(() => {
    if (shown >= signals.length) return;
    const timer = window.setTimeout(() => setShown(prev => prev + 1), TRAIT_REVEAL_MS);
    return () => window.clearTimeout(timer);
  }, [shown, signals.length]);

  if (signals.length === 0) return null;

  return (
    <div className="ob-profile__traits" data-testid="onboarding-scan-persona-traits">
      {signals.slice(0, shown).map(signal => {
        const source = personaSourceLabel(signal.source);
        return (
          <span
            key={`${signal.source}:${signal.tell}`}
            data-testid="onboarding-scan-persona-trait"
            className="ob-trait text-foreground"
          >
            {signal.tell}
            {source && <span className="ob-trait__src">{source}</span>}
          </span>
        );
      })}
    </div>
  );
}

interface Props {
  personas: CustomerPersonaCard[];
  expectedTotal: number;
  progressKey?: string;
  /** What the agents say they are doing, shown until the first persona lands. */
  liveMessages?: string[];
  audienceFacts?: AudienceFact[];
}

function PersonaProfile({ persona, position }: { persona: CustomerPersonaCard; position: number }) {
  return (
    <div className="ob-profile" key={persona.index} data-testid="onboarding-scan-persona-profile">
      <div className="ob-profile__portrait" aria-hidden="true">
        <svg className="ob-profile__ring" viewBox="0 0 96 96">
          <circle cx="48" cy="48" r="46" pathLength="100" />
        </svg>
        <span className="ob-profile__avatar">{position}</span>
      </div>
      <div className="ob-profile__body">
        <div className="ob-profile__archetype text-foreground" data-testid="onboarding-scan-persona-archetype">
          {persona.segment}
        </div>
        {persona.scope && (
          <div className="ob-profile__who">{persona.scope}</div>
        )}
        {persona.description && (
          <p className="ob-profile__summary text-muted-foreground">{persona.description}</p>
        )}
        <PersonaTraits signals={persona.signals ?? []} />
      </div>
    </div>
  );
}

export default function CustomersPanel({ personas, expectedTotal, progressKey, liveMessages, audienceFacts }: Props) {
  const [picked, setPicked] = useState<number | null>(null);

  const latest = personas.length - 1;
  const viewing = picked !== null && picked <= latest ? picked : latest;
  const current = personas[viewing];
  const total = Math.max(expectedTotal, personas.length);

  // An empty stack of numbered cards says nothing. The rail earns its place only
  // once there is a persona to put in it.
  if (!current) return <CustomersWaiting progressKey={progressKey} liveMessages={liveMessages} facts={audienceFacts} />;

  return (
    <div className="ob-personas" data-testid="onboarding-scan-personas">
      <PersonaProfile persona={current} position={viewing + 1} />

      <div className="ob-idrail" data-testid="onboarding-scan-persona-rail">
        {Array.from({ length: total }, (_, i) => {
          const known = personas[i];
          const state = i < latest ? 'done' : i === latest ? 'live' : 'pending';
          return (
            <button
              type="button"
              key={i}
              disabled={!known}
              aria-pressed={i === viewing}
              /* design-system.css strips border and background from any button
                 inside .th that does not opt out, which would erase the card. */
              data-th-button="true"
              onClick={() => known && setPicked(i)}
              className={cn('ob-idcard', `ob-idcard--${state}`, i === viewing && 'ob-idcard--sel')}
            >
              <span className="ob-idcard__avatar">{i + 1}</span>
              {known && (
                <span className="ob-idcard__meta">
                  <span className="ob-idcard__name">{known.segment}</span>
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
